import { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useFormikContext } from "formik";
import debounce from "lodash-es/debounce";

// Actions
import { parseFetchOptions } from "src/state/user/actions";

// Config
import { getStatementUrl } from "src/config/apiConfig";

// Hooks
import { useAppInsights } from "src/utils/telemetry/AppInsights";

// Utils
import statements from "src/utils/statements";

const AUTOSAVE_DELAY_MS = 2000;

// Renders nothing: mounted once alongside FormContent/FormReview for the
// lifetime of StatementForm, so it keeps saving on value changes regardless
// of which page/review step the user is on, and works the same for both
// single-page and multi-page statements. Failures are tracked but swallowed
// silently - a missed autosave shouldn't interrupt the user, since the final
// submit in FormReview still sends the complete, authoritative values.
const DraftAutosave = ({ schema }) => {
  const { values } = useFormikContext();
  const { caseId, itemId } = useParams();
  const dispatch = useDispatch();
  const { trackException } = useAppInsights();
  const isFirstRender = useRef(true);

  const saveDraft = useMemo(
    () => vals => {
      const responses = statements.getDraftResponses(schema, vals);
      if (Object.keys(responses).length === 0) return;

      dispatch(
        parseFetchOptions(
          async ({ headers: { Authorization, ...headers } }) => {
            const url = getStatementUrl(caseId, itemId);

            try {
              const res = await fetch(url, {
                method: "PATCH",
                headers: {
                  ...headers,
                  Authorization,
                  "Content-Type": "application/merge-patch+json"
                },
                body: JSON.stringify({ responses })
              });
              if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
              }
            } catch (err) {
              trackException(
                `Failed to autosave draft statement ${itemId} for case ${caseId}, ${err}`
              );
            }
          }
        )
      );
    },
    [dispatch, caseId, itemId, schema, trackException]
  );

  const debouncedSave = useMemo(
    () => debounce(saveDraft, AUTOSAVE_DELAY_MS),
    [saveDraft]
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    debouncedSave(values);
  }, [values, debouncedSave]);

  useEffect(() => () => debouncedSave.flush(), [debouncedSave]);

  return null;
};

DraftAutosave.propTypes = {
  schema: PropTypes.shape({
    properties: PropTypes.shape({})
  }).isRequired
};

export default DraftAutosave;
